/**
 * The water stage's three variants (the "Stage · Water light" frames, Figma 42:9 and its
 * siblings), as drawn: each in its own frame's pixels. Stage picks one by its size and shape.
 * Ellipses: x, y, w, h, blur. Rings: x, y, w, h, rotation (clockwise). Rays pivot on their
 * top-left corner: x, w, rotation (clockwise). Bubbles: x, y, size, inner dot size.
 */
export interface WaterLayout {
  frame: { w: number; h: number };
  deep: readonly [number, number, number, number, number];
  aqua: readonly [number, number, number, number, number];
  seafoam: readonly [number, number, number, number, number];
  caustics: {
    stroke: number;
    blur: number;
    rings: readonly (readonly [number, number, number, number, number])[];
  };
  rays: { top: number; blur: number; shafts: readonly (readonly [number, number, number])[] };
  bubbles: readonly (readonly [number, number, number, number])[];
}

export const WATER_LAYOUTS = {
  hero: {
    frame: { w: 1440, h: 720 },
    deep: [72, -252, 1296, 504, 160],
    aqua: [360, -144, 720, 360, 140],
    seafoam: [864, 36, 504, 216, 140],
    caustics: {
      stroke: 6,
      blur: 20,
      rings: [
        [259, 43, 180, 40, 12],
        [605, 22, 240, 50, -8],
        [950, 60, 200, 36, 6],
        [432, 121, 160, 30, -14],
        [1123, 130, 220, 44, 10],
        [792, 95, 140, 26, -4],
      ],
    },
    rays: {
      top: -72,
      blur: 18,
      shafts: [
        [910, 60, 17],
        [922, 97, 9],
        [968, 64, 0],
        [979, 101, -9],
        [1026, 68, -17],
      ],
    },
    bubbles: [
      [880, 560, 10, 3],
      [900, 500, 6, 0],
      [1180, 600, 12, 4],
      [1200, 540, 7, 0],
      [1060, 420, 5, 0],
      [760, 470, 4, 0],
      [1300, 460, 8, 2],
    ],
  },
  pack: {
    frame: { w: 500, h: 440 },
    deep: [25, -154, 450, 308, 96],
    aqua: [125, -88, 250, 220, 84],
    seafoam: [300, 22, 175, 132, 84],
    caustics: {
      stroke: 3.6,
      blur: 14,
      rings: [
        [90, 26, 108, 24, 12],
        [210, 13, 144, 30, -8],
        [330, 37, 120, 22, 6],
        [150, 74, 96, 18, -14],
        [390, 79, 132, 26, 10],
        [275, 58, 84, 16, -4],
      ],
    },
    rays: {
      top: -44,
      blur: 10.8,
      shafts: [
        [192, 36, 17],
        [201, 58, 9],
        [231, 38, 0],
        [240, 61, -9],
        [270, 41, -17],
      ],
    },
    bubbles: [
      [120, 330, 9, 3],
      [136, 290, 5, 0],
      [380, 360, 11, 3],
      [396, 310, 6, 0],
      [300, 200, 4, 0],
    ],
  },
  mobile: {
    frame: { w: 390, h: 520 },
    deep: [20, -182, 351, 364, 72],
    aqua: [98, -104, 195, 260, 63],
    seafoam: [234, 26, 137, 156, 63],
    caustics: {
      stroke: 3,
      blur: 14,
      rings: [
        [70, 31, 81, 18, 12],
        [164, 16, 108, 23, -8],
        [257, 44, 90, 16, 6],
        [117, 87, 72, 14, -14],
        [304, 94, 99, 20, 10],
        [215, 69, 63, 12, -4],
      ],
    },
    rays: {
      top: -52,
      blur: 8.1,
      shafts: [
        [148, 27, 17],
        [156, 44, 9],
        [181, 29, 0],
        [189, 45, -9],
        [213, 31, -17],
      ],
    },
    bubbles: [
      [60, 400, 8, 2],
      [72, 360, 5, 0],
      [320, 430, 10, 3],
      [330, 380, 6, 0],
      [250, 280, 4, 0],
    ],
  },
} as const satisfies Record<string, WaterLayout>;
